import type { IAppConfig, IAppEnvironment } from "@/types/configTypes";

/**
 * App Configuration Service
 * Loads environment-specific configuration from public/config files
 */

let cachedEnv: IAppEnvironment | null = null;
let pendingPromise: Promise<IAppEnvironment> | null = null;

async function getAppEnvironmentData(): Promise<IAppEnvironment> {
  if (cachedEnv) {
    return cachedEnv;
  }

  if (pendingPromise) {
    return pendingPromise;
  }

  pendingPromise = (async (): Promise<IAppEnvironment> => {
    try {
      // Fetch main config to determine environment
      const configResponse = await fetch("/config/config.json");
      if (!configResponse.ok) {
        throw new Error("Failed to fetch config.json");
      }
      const appConfig: IAppConfig = await configResponse.json();
      const { env } = appConfig;

      // Fetch environment-specific config
      const envConfigPath = `/config/${env}Config.json`;
      const envConfigResponse = await fetch(envConfigPath);
      if (!envConfigResponse.ok) {
        throw new Error(`Failed to fetch ${envConfigPath}`);
      }
      const envConfig: IAppEnvironment = await envConfigResponse.json();

      cachedEnv = envConfig;
      pendingPromise = null;
      return envConfig;
    } catch (error) {
      console.error("Error loading app configuration:", error);
      pendingPromise = null;
      throw error;
    }
  })();

  return pendingPromise;
}

function clearConfigCache(): void {
  cachedEnv = null;
  pendingPromise = null;
}

export const appConfigService = {
  getAppEnvironmentData,
  clearConfigCache,
};
